import { html, css, LitElement, unsafeCSS } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { Map, View } from 'ol'
import OSM from 'ol/source/OSM';
import TileLayer from 'ol/layer/Tile'
import olStylesText from 'ol/ol.css';
import { Style, Icon, Text, Fill, Stroke } from 'ol/style'
import Point from 'ol/geom/Point'
import { Coordinate } from 'ol/coordinate';
import VectorSource from 'ol/source/Vector'
import VectorLayer from 'ol/layer/Vector'
import { Modify, defaults as defaultInteractions } from 'ol/interaction'
import Feature from 'ol/Feature'
import { fromLonLat, toLonLat } from 'ol/proj'
import Overlay from 'ol/Overlay';
import { getArtists } from './artistutils';
import { getWidth } from 'ol/extent';
import { getIconStyles } from './artistmapstyles';
import { favouritesEl } from './favourites';
import FullScreen from 'ol/control/FullScreen'
import { defaults as defaultControls } from 'ol/control/defaults';

export class ArtistMap extends LitElement {
    static styles = [
        unsafeCSS(olStylesText),
        css`
*, ::after, ::before {
    box-sizing: border-box;
}
.tooltip {
  position: absolute;
  background-color: white;
  padding: 5px;
  border: 1px solid #ccc;
  border-radius: 4px;
  pointer-events: none; /* Prevents the tooltip from flickering when the mouse enters it */
  display: none;
}
.ol-full-screen button {
  cursor: pointer;
}
:host { display: block; }
.map { 
    width: 100%; 
    height: 500px; 
    background: #eee; 
}
@media(min-width: 768px) {
    .map {
        height: 800px;
    }
}
.ol-popup, .map, .ol-viewport {
    transition: none !important;
}
/* The main container needs to allow absolute positioning for the arrow */
.ol-popup {
    position: absolute;
    background-color: white;
    padding: 15px;
    border-radius: 10px;
    border: 1px solid #fff;
    bottom: 12px;
    left: -50px;
    min-width: 232px;
    font-weight: 300;
    font-size: 13px;
    filter: drop-shadow(0 1px 4px rgba(0,0,0,0.2));
}

@media (min-width: 992px) {
    .ol-popup {
        min-width: 320px;
    }
}

    /* The "Outer" triangle (the border) */
    .ol-popup::before {
        border-top: 10px solid white;
        border-right: 10px solid transparent;
        border-left: 10px solid transparent;
        content: "";
        height: 0;
        width: 0;
        position: absolute;
        bottom: -9px; /* Positions it just below the border */
        left: 40px; /* Aligns it under the popup */
    }

    /* The "Inner" triangle (the white fill) */
    .ol-popup::after {
        border-top: 10px solid white;
        border-right: 10px solid transparent;
        border-left: 10px solid transparent;
        content: "";
        height: 0;
        width: 0;
        position: absolute;
        bottom: -9px;
        left: 40px;
    }
    .ol-popup-closer { text-decoration: none; position: absolute; top: 2px; right: 8px; color: #333; }
@media (min-width: 992px) {
    .d-md-flex {
        display: flex;
    }
}
.listingid {
    padding-left: 16px;
    font-size: 16px;
}
.title {
    font-size: 18px;
    font-weight: bold;
    margin-top: 8px;
    margin-bottom: 8px;
}
a {
    color: #333;
    text-decoration: none;
}
a.add-to-favorites, a.remove-from-favourites {
    text-decoration: underline; 
}
@media (min-width: 768px) {
    .title {
        font-size: 24px;
        margin-top: 10px;
        margin-bottom: 10px;
    }
}
.medium, .address {
    margin-bottom: 10px;
}
.infowindow {
    max-height: 400px;
    overflow-y: auto;
}
.infowindow .item:not(:first-child) {
    margin-top: 30px;
}
.medium-key {
    padding-top: 30px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-column-gap: 16px;
    grid-row-gap: 16px;
}
.medium-key > * {
    display: flex;
    align-items: center;
}

.medium-key img {
    margin-right: 8px;
}
@media (min-width: 768px) {
    .medium-key {
        grid-template-columns: 1fr 1fr 1fr;
    }
}
        `
    ];

    static get properties() {
        return {
            isLoading: { type: Boolean },
            icons: { type: Object },
            iconHtml: { type: String },
            artistSelectionCallback: { type: Function },
        };
    }

    map = null
    markerSVG = `<svg xmlns="http://www.w3.org/2000/svg" width="512" height="512" viewBox="0 0 512 512"><path fill="red" d="M256 0C160 0 82 78 82 174c0 101 80 164 123 239 42 75 42 102 51 102s9-27 51-99c43-75 123-138 123-239C430 78 352 0 256 0zm0 240c-37 0-67-30-67-67 0-37 30-67 67-67s67 30 67 67c0 37-30 67-37 67z"/><circle cx="256" cy="174" r="67" fill="rgba(0,0,0,0.01)"/></svg>`;
    markerSVGUrl = 'data:image/svg+xml;base64,' + btoa(this.markerSVG);
    markerStyle = new Style({
        image: new Icon({
            anchor: [0.5, 1],
            anchorXUnits: 'fraction',
            anchorYUnits: 'fraction',
            src: this.markerSVGUrl,
            scale: 0.05
        }),
    });
    marker = new Feature();
    markerLayer = new VectorLayer({
        source: new VectorSource(),
        declutter: false,
        renderOptions: {
            willReadFrequently: true
        }
    });

    infowindow = undefined

    constructor() {
        super();
        this.isLoading = true;
        this.artists = getArtists().filter(artist => {
            return artist.coords
        });
        this.iconHtml = ""
        this.mapReadyPromise = new Promise((resolve) => {
            this._resolveMapReady = resolve;
        });
    }


    async getIconStyles() {
        this.isLoading = true
        this.icons = await getIconStyles()
        this.icons.forEach(icon => {
            this.iconHtml += `<div><img src="${icon.url}" alt="${icon.medium}">${icon.medium}</div>`
        })

        //console.log("Loaded icons:", this.icons)
        this.isLoading = false
    }

    fitBounds() {
        this.map.updateSize();
        const extent = this.markerLayer.getSource().getExtent();
        //console.log("Fitting bounds to extent:", extent)
        if (!extent || extent[0] === Infinity) return;
        this.map.getView().fit(extent, {
            maxZoom: 16,
            padding: [50, 50, 50, 50],
        });

    }

    show() {
        this.fitBounds()
    }

    setArtists(artists) {
        this.artists = artists
        if (this.infowindow) {
            this.infowindow.setPosition(undefined);
        }
        this.clearMarkers()
        this.setupMarkers()
        this.requestUpdate();
    }

    clearMarkers() {
        const source = this.markerLayer.getSource();
        source.clear();
    }

    getMarkerCords(coords) {
        return fromLonLat([coords.longitude, coords.latitude])
    }

    setupInfoWindow() {
        const container = this.shadowRoot.getElementById('popup')
        this.infocontent = this.shadowRoot.getElementById('popup-content')
        const closer = this.shadowRoot.getElementById('popup-closer')

        this.infowindow = new Overlay({
            element: container,
            autoPanMargin: 150,
            //positioning: 'top-center',
            // Fine-tune the vertical gap (up 15px to account for the arrow length)
            offset: [0, -15],
            autoPan: true
        });
        this.map.addOverlay(this.infowindow)

        closer.onclick = () => {
            this.infowindow.setPosition(undefined);
            return false;
        }
    }

    getInfoWindowContent(features) {
        let result = "";
        let addressHtml = "";
        features.forEach(feature => {

            const artistsAtLocation = feature.get('artists');
            if (artistsAtLocation) {
                console.log(artistsAtLocation)
                let names = ''
                artistsAtLocation.forEach(artist => {
                    const name = artist.name
                    const logo = artist.logo
                    const listingNumber = artist.listingNumber
                    const medium = artist.medium
                    const address = artist.address
                    const nameinurl = artist.nameinurl
                    const id = artist.id
                    const isFavourite = favouritesEl.favourites.includes(id);
                    const listingHtml = listingNumber && parseInt(listingNumber) > 0 ? `<div class="listingid">Guide Listing ID: #${listingNumber}</div>` : "";
                    const mediumHtml = medium ? `<div class="medium"><strong>Medium:</strong> ${medium}</div>` : "";
                    addressHtml = address ? `<div class="address">${address}</div>` : "";
                    const imageHtml = logo ? `<div><img style="width:190px;height:120px;object-fit: cover;" src="${logo}" alt="${name}"/></div>` : "";
                    result += `
            <div class="item">
                <div class="d-md-flex">
                    ${imageHtml}
                    ${listingHtml}
                </div>
                <div class="title"><a href="./?artist=${nameinurl}">${name}</a></div>
                ${mediumHtml}
                ${addressHtml}
                ${isFavourite ? `<a href="removefavourites" data-id="${id}" data-name="${name}" class="remove-from-favourites">Remove from Favorites</a>` : `<a href="addtofavourites" data-id="${id}" data-name="${name}"  class="add-to-favorites">Add to Favorites</a>`}
                
            </div>
                `
                })
            }
        })
        const multiple = features.length > 1 ? ` multiple` : ""
        result = `<div class="infowindow${multiple}">${result}</div>`
        return result;
    }

    handleAddRemoveLink(link) {
        console.log(link)
        const artistId = parseInt(link.dataset.id)
        if (link.classList.contains("add-to-favorites")) {
            favouritesEl.addFavouriteClickEvent(link.dataset.name)
            favouritesEl.addFavourite(artistId, () => {
                link.classList.remove("add-to-favorites");
                link.classList.add("remove-from-favourites");
                link.href = "removefavourites"
                link.textContent = "Remove from Favourites";
            })
        } else if (link.classList.contains("remove-from-favourites")) {
            favouritesEl.removeFavouriteClickEvent(link.dataset.name)
            favouritesEl.removeFavourite(artistId, () => {
                link.classList.remove("remove-from-favourites");
                link.classList.add("add-to-favorites");
                link.href = "addtofavourites"
                link.textContent = "Add to Favourites";
            })
        }
    }


    handleMapClick(source, feature) {
        if (feature) {
            const coordinates = feature.getGeometry().getCoordinates();

            // get any artists with the same coordinates (for multiple artists at the same location)
            const matchingFeatures = source.getFeaturesAtCoordinate(coordinates);
            //console.log("Matching features at coordinates:", matchingFeatures)

            this.infocontent.innerHTML = this.getInfoWindowContent(matchingFeatures)
            this.infocontent.querySelectorAll(".title a").forEach(link => {
                link.addEventListener("click", (event) => {
                    event.preventDefault();
                    if (document.fullscreenElement) {
                        document.exitFullscreen()
                            .then(() => console.log("Exited full screen"))
                            .catch((err) => console.error(err));
                    }
                    if (this.artistSelectionCallback) {
                        this.artistSelectionCallback(link.href)
                    }
                    //history.pushState(null, null, link.href)
                });
            });

            this.infocontent.querySelectorAll("a.add-to-favorites").forEach(link => {
                link.addEventListener("click", (event) => {
                    event.preventDefault();
                    this.handleAddRemoveLink(link)
                });
            });
            this.infocontent.querySelectorAll("a.remove-from-favourites").forEach(link => {
                link.addEventListener("click", (event) => {
                    event.preventDefault();
                    this.handleAddRemoveLink(link)

                });
            });
            this.infowindow.setPosition(undefined);
            this.infowindow.setPosition(coordinates);
            this.infowindow.panIntoView();
        } else {
            this.closePopup()
        }
    }

    setupMarkers() {
        const mapContainer = this.shadowRoot.querySelector('.map');
        const source = this.markerLayer.getSource()
        //console.log("Artists:", artists)
        if (this.artists) {
            
            const groupsByLocation = {};

            this.artists.forEach(artist => {
                if (artist.coords) {
                    const coords = JSON.parse(artist.coords);
                    const markerCoords = this.getMarkerCords(coords);
                    // Create a unique string key based on the X and Y coordinates
                    const key = `${markerCoords[0]},${markerCoords[1]}`;

                    if (!groupsByLocation[key]) {
                        groupsByLocation[key] = {
                            coordinates: markerCoords,
                            artistsList: []
                        };
                    }
                    // Add this artist's raw details to the array for this location
                    groupsByLocation[key].artistsList.push(artist);
                }
            });

            Object.values(groupsByLocation).forEach(group => {
                const totalArtistsAtLocation = group.artistsList.length;

                // Grab the first artist to use for the single icon lookup
                const firstArtist = group.artistsList[0];

                const marker = new Feature({
                    geometry: new Point(group.coordinates),
                    // CRITICAL: Save the entire array of artists inside this feature!
                    artists: group.artistsList,
                    // Optional: keeping fallback single properties if your code expects them
                    id: firstArtist.id,
                    name: firstArtist.name
                });

                // 3. Determine the correct icon based on the group count
                const defaultIcon = this.icons.find(icon => icon.medium === 'Other');
                const multipleIcon = this.icons.find(icon => icon.medium === 'Multiple/Exhibition Space');

                let icon = this.icons ? this.icons.find(icon => icon.medium === firstArtist.medium) : defaultIcon;

                // If more than 1 artist shares this spot, force the multiple icon
                if (totalArtistsAtLocation > 1) {
                    icon = multipleIcon;
                }

                if (icon) {
                    marker.setStyle((feature, resolution) => {
                        const currentZoom = this.map.getView().getZoomForResolution(resolution);
                        const dynamicScale = currentZoom >= 12 ? 1.6 : 1;
                        const styles = [];
                        let text = null;

                        // If there are multiple artists, overlay the count
                        if (totalArtistsAtLocation > 1) {
                            const dynamicOffsetY = currentZoom >= 12 ? -19 : -12;
                            const fontSize = currentZoom >= 12 ? 18 : 12;

                            text = new Text({
                                text: totalArtistsAtLocation.toString(), // Exact total count
                                font: `bold ${fontSize}px sans-serif`,
                                fill: new Fill({ color: '#fff' }),
                                stroke: new Stroke({
                                    color: 'rgba(0, 0, 0, 0.6)',
                                    width: 6
                                }),
                                offsetY: dynamicOffsetY,
                                textAlign: 'center',
                                textBaseline: 'middle',
                                declutterMode: 'none' // Keeps text strictly locked to icon depth layer
                            });
                        }

                        styles.push(new Style({
                            image: new Icon({
                                anchor: [0.5, 1],
                                anchorXUnits: 'fraction',
                                anchorYUnits: 'fraction',
                                src: icon.url,
                                scale: dynamicScale
                            }),
                            text: text,
                            zIndex: 100 // Ensures consistent stack order
                        }));

                        return styles;
                    });
                } else {
                    marker.setStyle(this.markerStyle);
                }

                source.addFeature(marker);
            });

        }
        this.fitBounds()

    }

    closePopup() {
        this.infowindow.setPosition(undefined);
    }

    get updateComplete() {
        return(async () => {
            // Wait for Lit's normal DOM update first
            await super.updateComplete;
            // Then wait for OpenLayers to render
            await this.mapReadyPromise;
            return true;
        })();
    }

    async firstUpdated() {
        await this.getIconStyles()
        //console.log("Icons loaded:", this.icons)
        const mapContainer = this.shadowRoot.querySelector('.map');
        this.map = new Map({
            target: mapContainer,
            interactions: defaultInteractions({
                mouseWheelZoom: false
            }),
            controls: defaultControls().extend([
                new FullScreen()
            ]),
            layers: [
                new TileLayer({
                    source: new OSM(),
                }),
            ],
            view: new View({
                center: fromLonLat([175.0032646, -40.91553427]),
                zoom: 12,
            }),
            rendererOptions: {
                willReadFrequently: true
            }
        });
        this.setupInfoWindow()
        this.map.addLayer(this.markerLayer)

        this.setupMarkers();
        this._handleEsc = (event) => {
            if (event.key === 'Escape') {
                this.closePopup();
            }
        };
        this.map.on('singleclick', (evt) => {
            const feature = this.map.forEachFeatureAtPixel(evt.pixel, (f) => f);
            const source = this.markerLayer.getSource()
            this.handleMapClick(source, feature);
        })

        this._resolveMapReady();

        let pmTimer = null;

        this.map.on('pointermove', (evt) => {
            if (pmTimer) return;

            pmTimer = setTimeout(() => {
                //const hit = this.map.hasFeatureAtPixel(evt.pixel);
                const tooltipElement = this.shadowRoot.getElementById('tooltip');
                const features = this.map.getFeaturesAtPixel(evt.pixel)
                if (features && features.length) {
                    const feature = features[0]
                    const artistsAtLocation = feature.get('artists');
                    if (artistsAtLocation && artistsAtLocation.length) {
                        name = ''
                        artistsAtLocation.forEach(artist => {
                            name += (name == '' ? '' : ', ') + artist.fullName
                        })
                        if (name.length) {
                            // Show tooltip and set text
                            tooltipElement.innerHTML = name;
                            tooltipElement.style.display = 'block';
                            // Position the tooltip near the mouse
                            tooltipElement.style.left = (evt.pixel[0] + 10) + 'px';
                            tooltipElement.style.top = (evt.pixel[1] + 10) + 'px';
                        } else {
                            tooltipElement.style.display = 'none';
                        }
                    } else {
                        tooltipElement.style.display = 'none';
                    }
                } else {
                    // Hide tooltip if not hovering
                    tooltipElement.style.display = 'none';
                }

                const hit = this.map.hasFeatureAtPixel(evt.pixel, {
                    layerFilter: (layer) => {
                        // Return true ONLY for your interactive vector layer
                        // All other layers bypass the heavy hit-detection engine completely
                        return layer === this.markerLayer;
                    }
                });

                const mapTarget = this.map.getTargetElement();
                if (mapTarget) {
                    mapTarget.style.cursor = hit ? 'pointer' : '';
                }

                pmTimer = null;
            }, 100); // 100ms reduces the readback load to a comfortable 10fps
        });

        document.addEventListener('keydown', this._handleEsc);
    }

    render() {
        return html`${this.isLoading
            ? html`<div class="spinner">🌀</div>` : html`<div style="position:relative">
                <div class="map"></div>
                <div id="tooltip" class="tooltip"></div>
                <div id="popup" class="ol-popup">
                  <a href="#" id="popup-closer" class="ol-popup-closer">✖</a>
                  <div id="popup-content"></div>
                </div>
                <div class="medium-key">${unsafeHTML(this.iconHtml)}</div>`}
            </div>`;
    }
}
customElements.define('artist-map', ArtistMap);